import { PrintoutOrientation } from "../../../utils/models";
import type { MonthlyExportSettingsValue, PersistedMonthlyExportSettings, WeeklyExportSettingsValue } from "./types";

export const SCHEDULE_EXPORT_TEST_ID = "schedule-export";

/** Debounce before rebuilding a preview, so holding a date stepper doesn't rebuild per tick. */
export const PREVIEW_DEBOUNCE_MS = 250;

/**
 * How long the preview waits after becoming visible before it builds anything.
 *
 * `MultiSizePopup` animates both opening and maximizing with a spring (damping 30, stiffness 400),
 * which settles a little under 400ms. A multi-page PDF render started inside that window holds the
 * main thread and stalls the animation — the popup looked frozen halfway through maximizing.
 */
export const PREVIEW_SETTLE_MS = 400;

/** Matches the other export modals so the family reads the same when maximized. */
export const MAX_CONTENT_WIDTH = "100%";

export const MAX_MINIMIZED_CONTENT_HEIGHT = 900;

/**
 * Ceiling for `fitToWidth` / `fitToHeight`, in whole pages.
 *
 * Mirrors `MONTHLY_EXPORT_MAX_FIT_PAGES` in `backend/models/authn.py`, which bounds BOTH the settings
 * document (`StaffingMonthlyExportSettings`) and the download's query params
 * (`backend/routes/downloads.py`). Enforced here too, or a typed page count past the ceiling 422s the
 * preview request and the settings PUT at once — the panel would show a save failure for a value it
 * had just accepted.
 */
export const MAX_FIT_PAGES = 20;

/** Matches the Daily Printout panel, which is where head nurses already know these inputs from. */
export const NUMERIC_INPUT_WIDTH = 90;
export const DIMMED_OPACITY = 0.4;

/**
 * Ceiling for the settings column, not its width.
 *
 * The column sizes itself to its widest control — the date range, which must keep both pickers on one
 * row. Letting the browser measure that is the only way to be right in every locale, since native date
 * inputs size to their own content and the weekly labels ("Start date (Sunday)") are longer in Spanish
 * and Hebrew. This only stops a long sentence, like the PTO warning, from stretching the column past
 * the width the Profile weekly export uses for the same job.
 */
export const SETTINGS_PANEL_MAX_WIDTH = 500;

export enum ScheduleExportTab {
	Weekly = "weekly",
	Monthly = "monthly",
}

/**
 * DEFAULT: gaps and hire date default OFF (APP-337): ops asked for a page they can post on a wall, and both
 * add rows/columns that only help while a schedule is still being filled. Schedulers were being
 * told to untick them every time. Time off defaults off for the same reason, and because it now
 * starts from a clean slate instead of inheriting whatever the Daily Printout panel last saved.
 *
 * Must stay in sync with `DEFAULT_STAFFING_MONTHLY_EXPORT_SETTINGS` in `backend/models/authn.py` and,
 * through it, the `ADD COLUMN` default in
 * `database/Changes/demo_needed/2026_08_20_add_staffing_export_settings.sql` (that migration
 * already ran and is not edited for the `sortEntries` addition — its literal default predates the
 * field and is stale, which is fine: it only ever seeded brand-new rows, and the backend backfills
 * a missing `sortEntries` from the legacy `sortBy` on read regardless).
 * `scheduleExportSettings.spec.ts` pins this side of that agreement.
 */
export const DEFAULT_MONTHLY_SETTINGS: MonthlyExportSettingsValue = {
	// DEPRECATED / VESTIGIAL — see `MonthlySortBy`'s comment in types.ts. Never surfaced in the
	// panel; kept only because the backend's `StaffingMonthlyExportSettings.sortBy` still requires it.
	sortBy: "location",
	// Location-first, then Role, then Name — reproduces today's hardcoded tuple sort exactly (Role
	// ran as a hidden tiebreaker before this control existed). Mirrors `DEFAULT_MONTHLY_SORT_BY` in
	// `backend/models/authn.py`.
	sortEntries: [
		{ column: "location", direction: "asc" },
		{ column: "role", direction: "asc" },
		{ column: "name", direction: "asc" },
	],
	// DEFAULT: today's unconditional behaviour, so an existing user's printout does not reshape itself
	// on upgrade. The ticket asks for shift-time separation to be the default view; it already is.
	shiftTimeSeparation: "sections",
	// DEFAULT: today's unconditional behaviour (numeric shift length) — an existing user's printout
	// does not reshape itself on upgrade.
	shiftCellDisplay: "hours",
	printGaps: false,
	showHireDate: false,
	recurringOnly: false,
	// DEFAULT: off, so the printout keeps listing everyone until a scheduler opts out of agency staff.
	hideAgency: false,
	showPtoSheet: false,
	// DEFAULT: a month of date columns needs the wide edge of the page (AC #1).
	orientation: PrintoutOrientation.Landscape,
	fitToPage: true,
	// Fit every date column to one page across, then let employees flow down as many pages as needed.
	fitToWidth: 1,
	fitToHeight: 0,
	// Vestigial, and read by nothing — see `MonthlyExportSettingsValue.wrapText`. Left at the value
	// every stored document was backfilled with so a save round-trips unchanged.
	wrapText: false,
};

/** The stored monthly document: the panel's values plus the preview-measured column widths. */
export const DEFAULT_PERSISTED_MONTHLY_SETTINGS: PersistedMonthlyExportSettings = {
	...DEFAULT_MONTHLY_SETTINGS,
	columnWidths: {},
};

/**
 * DEFAULT: the weekly tab's one option, kept here beside the monthly defaults so both documents are in
 * one place. Off for the same reason: it is the export's own choice now, not whatever the Daily
 * Printout panel happened to save.
 */
export const DEFAULT_WEEKLY_SETTINGS: WeeklyExportSettingsValue = {
	showPtoSheet: false,
};
